<script lang="ts">
  import { CommonTexts } from '../../services/CommonTexts';
  import RichText from '../../components/content/RichText.svelte';
  import { UsersTexts } from './UsersTexts';
  import { LayerHandler } from '../../handlers/LayerHandler';
  import Headline from '../../components/content/Headline.svelte';
  import Content from '../../components/content/Content.svelte';
  import Form from '../../components/forms/Form.svelte';
  import FormInput from '../../components/forms/FormInput.svelte';
  import FormButton from '../../components/forms/FormButton.svelte';
  import FormButtonLine from '../../components/forms/FormButtonLine.svelte';
  import ErrorMessages from '../../components/boxes/ErrorMessages.svelte';
  import NoticeBox from '../../components/boxes/NoticeBox.svelte';
  import { ApiService } from '../../api/services/ApiService';
  import type { ApiResult } from '../../api/types/api.types';
  import { PageService } from '../../services/PageService.svelte';
  import { AppInfoService } from '../../services/AppInfoService.svelte';
  import { UserService } from '../../services/UserService.svelte';

  /**
   * One form that grows step by step: user → password → code of the second factor. Two branches: an unknown name
   * of the DWC domain can register (code by mail, then logged in), and "Passwort vergessen" sends a code before a new
   * password. Every step is one request (ApiService); the inputs of earlier steps stay visible, read-only.
   */
  type Step = 'identify' | 'register' | 'registerCode' | 'password' | 'tfa' | 'resetCode' | 'resetPassword';

  // -
  const Layer = new LayerHandler();
  const MyLayer = Layer.getContentLayer();

  // -
  let InStep = $state<Step>('identify');
  let Username = $state('');
  let Password = $state('');
  let Code = $state('');
  let NewPassword = $state('');
  let NewPasswordRepeat = $state('');
  let RegisterEmail = $state('');
  let IsLoading = $state(false);
  let Errors = $state<string[]>([]);
  let Notice = $state<string | null>(null);

  // -
  const IsEmail = $derived(Username.includes('@'));
  const IsRegistering = $derived(InStep === 'register' || InStep === 'registerCode');
  const IsResetting = $derived(InStep === 'resetCode' || InStep === 'resetPassword');
  const ShowPassword = $derived(InStep === 'password' || InStep === 'tfa');
  const ShowCode = $derived(InStep === 'tfa' || InStep === 'registerCode' || InStep === 'resetCode' || InStep === 'resetPassword');
  const CodeLabel = $derived(InStep === 'tfa' ? UsersTexts.LoginCode : InStep === 'registerCode' ? UsersTexts.RegisterCode : UsersTexts.EmailCode);
  const SubmitLabel = $derived(
    {
      identify: UsersTexts.Next,
      register: UsersTexts.RegisterConfirm,
      registerCode: UsersTexts.Unlock,
      password: CommonTexts.Login,
      tfa: UsersTexts.Confirm,
      resetCode: UsersTexts.CodeCheck,
      resetPassword: UsersTexts.PasswordSave,
    }[InStep]
  );

  /** Moves on and clears what belongs to the next step. */
  function goTo(step: Step, notice: string | null = null): void {
    InStep = step;
    Code = step === 'resetPassword' ? Code : '';
    NewPassword = '';
    NewPasswordRepeat = '';
    Errors = [];
    Notice = notice;
  }

  /** Back to the start with an empty form. */
  function restart(): void {
    Password = '';
    RegisterEmail = '';
    goTo('identify');
  }

  /** Sends the browser to the sign-in at Microsoft; it comes back to `/microsoft` (MicrosoftCallback), which logs in. */
  async function loginWithMicrosoft(): Promise<void> {
    if (IsLoading || !AppInfoService.isMicrosoftAvailable) {
      return;
    }
    const Result = await call(ApiService.users.startMicrosoftLogin());
    if (Result?.body !== undefined) {
      IsLoading = true;
      window.location.assign(Result.body.url);
    }
  }

  function clickMicrosoft(): void {
    void loginWithMicrosoft();
  }

  /** Runs one request; on failure its messages are shown and false comes back. */
  async function call<TBody>(request: Promise<ApiResult<TBody>>): Promise<ApiResult<TBody> | null> {
    IsLoading = true;
    Errors = [];
    const Result = await request;
    IsLoading = false;
    if (!Result.isOk) {
      Errors = ApiService.listErrorTexts(Result);
      return null;
    }

    return Result;
  }

  async function identify(): Promise<void> {
    const Result = await call(ApiService.users.checkUser(Username));
    if (Result?.body === undefined) {
      return;
    }
    if (Result.body.isValid) {
      goTo('password');
      return;
    }
    if (Result.body.registerEmail !== undefined) {
      RegisterEmail = Result.body.registerEmail;
      goTo('register');
      return;
    }
    Errors = [UsersTexts.UserUnknown];
  }

  function clickPasswordReset(): void {
    void startPasswordReset();
  }

  async function startPasswordReset(): Promise<void> {
    if (IsLoading) {
      return;
    }
    const Result = await call(ApiService.users.resetPassword(Username));
    if (Result !== null) {
      Password = '';
      goTo('resetCode', UsersTexts.ResetCodeSent);
    }
  }

  async function submitStep(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (IsLoading) {
      return;
    }

    // -
    switch (InStep) {
      case 'identify':
        await identify();
        return;
      case 'register':
        if ((await call(ApiService.users.register(Username))) !== null) {
          goTo('registerCode', UsersTexts.format(UsersTexts.RegisterCodeSent, { email: RegisterEmail }));
        }
        return;
      case 'registerCode':
        if ((await call(ApiService.users.confirmRegistration(Username, Code))) !== null) {
          // - logged in now; the password comes by mail and has to be changed first (LyMain)
          await UserService.refresh();
          PageService.set('/');
        }
        return;
      case 'password': {
        const Result = await call(ApiService.users.login(Username, Password));
        if (Result?.body?.tfa !== undefined) {
          const tfa = Result.body.tfa;
          const msg = tfa.method === 'app' ? UsersTexts.TfaEnterCode : UsersTexts.format(UsersTexts.LoginCodeSent, { hint: tfa.hint });
          goTo('tfa', msg);
        }
        return;
      }
      case 'tfa':
        if ((await call(ApiService.users.confirmLogin(Code))) !== null) {
          // - LyMain shows the requested page as soon as whoAmI knows the user
          await UserService.refresh();
        }
        return;
      case 'resetCode':
        if ((await call(ApiService.users.checkPasswordReset(Username, Code))) !== null) {
          goTo('resetPassword');
        }
        return;
      case 'resetPassword':
        if (NewPassword !== NewPasswordRepeat) {
          Errors = [UsersTexts.PasswordsDiffer];
          return;
        }
        if ((await call(ApiService.users.finishPasswordReset(Username, Code, NewPassword))) !== null) {
          NewPassword = '';
          NewPasswordRepeat = '';
          goTo('password', UsersTexts.PasswordChangedLogin);
        }
        return;
    }
  }
</script>

<section class="contentLayer contentLayer{MyLayer} contentLayerChildAbs">
  <Headline state="brand">{IsRegistering ? UsersTexts.Register : IsResetting ? UsersTexts.PasswordNew : CommonTexts.Login}</Headline>

  <Content>
    {#if InStep === 'identify'}
      <p>{UsersTexts.LoginIntro}</p>
    {:else if InStep === 'register'}
      <p><RichText text={UsersTexts.RegisterQuestion} values={{ username: Username, email: RegisterEmail }} /></p>
    {:else if InStep === 'password'}
      <p>{UsersTexts.PasswordEnter}</p>
    {:else if InStep === 'resetPassword'}
      <p>{UsersTexts.ResetCodeValid}</p>
    {/if}
  </Content>

  {#if Notice !== null}
    <NoticeBox>
      {#snippet headline()}{UsersTexts.Hint}{/snippet}
      <p>{Notice}</p>
    </NoticeBox>
  {/if}
  <ErrorMessages errors={Errors} />

  <Form layerAbs onsubmit={submitStep}>
    <FormInput
      autofocus
      label={IsEmail ? UsersTexts.EmailAddress : UsersTexts.Username}
      name="username"
      autocomplete="username"
      required
      readonly={InStep !== 'identify'}
      bind:value={Username}>
      {#snippet htmlLabel()}
        {#if !IsEmail}<span style="opacity:.5;">{UsersTexts.OrEmail}</span>{/if}
      {/snippet}
    </FormInput>
    {#if ShowPassword}
      <FormInput autofocus type="password" label={UsersTexts.Password} name="password" required readonly={InStep !== 'password'} bind:value={Password} />
    {/if}
    {#if ShowCode}
      <FormInput autofocus type="code" label={CodeLabel} name="code" required readonly={InStep === 'resetPassword'} bind:value={Code} />
    {/if}
    {#if InStep === 'resetPassword'}
      <FormInput autofocus type="password" label={UsersTexts.PasswordNew} name="new_password" autocomplete="new-password" required bind:value={NewPassword} />
      <FormInput type="password" label={UsersTexts.PasswordNewRepeat} name="new_password_repeat" autocomplete="new-password" required bind:value={NewPasswordRepeat} />
    {/if}

    <FormButtonLine>
      <FormButton type="submit" disabled={IsLoading}>{SubmitLabel}</FormButton>
      {#if InStep === 'identify'}
        <FormButton type="button" state="gray" disabled={IsLoading || !AppInfoService.isMicrosoftAvailable} onclick={clickMicrosoft}>{UsersTexts.MicrosoftAccount}</FormButton>
      {/if}
      {#if InStep === 'password'}
        <FormButton state="gray" disabled={IsLoading} onclick={clickPasswordReset}>{UsersTexts.PasswordForgotten}</FormButton>
      {/if}
      {#if InStep !== 'identify'}
        <FormButton state="gray" disabled={IsLoading} onclick={restart}>{InStep === 'register' ? CommonTexts.No : UsersTexts.Back}</FormButton>
      {/if}
    </FormButtonLine>
  </Form>
</section>
